import type { AvailableFilters } from "@/features/companiesSearch/types"

import { startHolyLoader } from "holy-loader"
import { useRouter, useSearchParams } from "next/navigation"

import { USE_EXCLUSIVE_FILTER } from "@/features/companiesSearch/constants"
import { useLayoutContext } from "@/modules/landingSearch/components/Layout/LayoutContextProvider"

/**
 * Set the value to null to remove it.
 */
type AvailableFiltersValues = Partial<Record<AvailableFilters, string | null>>

const availableFilterNames: AvailableFilters[] = USE_EXCLUSIVE_FILTER
  ? ["businessTypes", "domains", "exclusive", "query", "sort"]
  : ["businessTypes", "domains", "query", "sort"]

export function useCompaniesFilters(): {
  navigateWithUpdatedQueryParams: (availableFilters: AvailableFiltersValues) => void
} {
  const { push } = useRouter()
  const nextSearchParams = useSearchParams()
  const { updateSearchLoadingStatus } = useLayoutContext()
  const updatedSearchParams = new URLSearchParams(nextSearchParams)

  function navigateWithUpdatedQueryParams(availableFilters: AvailableFiltersValues): void {
    updatedSearchParams.delete("page")

    const searchParamsBeforeUpdate = updatedSearchParams.toString()

    availableFilterNames.forEach(availableFilterName => {
      const value = availableFilters[availableFilterName]

      if (value && Array.isArray(value)) {
        updatedSearchParams.delete(availableFilterName)
        value.forEach(singleValue => {
          if (singleValue) {
            updatedSearchParams.append(availableFilterName, singleValue)
          }
        })
      } else if (value) {
        updatedSearchParams.set(availableFilterName, value)
      } else if (updatedSearchParams.has(availableFilterName) && value === null) {
        updatedSearchParams.delete(availableFilterName)
      }
    })

    const searchParamsAfterUpdate = updatedSearchParams.toString()

    if (searchParamsBeforeUpdate === searchParamsAfterUpdate) {
      return
    }

    startHolyLoader()
    updateSearchLoadingStatus(true)
    push(`?${updatedSearchParams.toString()}`)
  }

  return {
    navigateWithUpdatedQueryParams,
  }
}
